import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { whatsappApi } from "@/services/whatsapp-api";
import { formatDateTime } from "@/utils/dateUtils";
import { WhatsAppTranscriptDialog } from "./WhatsAppTranscriptDialog";

const AUTOR_STYLES: Record<string, string> = {
  CONTATO: "bg-muted",
  ATENDENTE: "bg-blue-50",
  IA: "bg-indigo-50",
  "NOTA INTERNA": "bg-amber-50 border border-dashed border-amber-300",
};

interface WhatsAppTrechoInlineProps {
  itemId: string;
  conversationId: string;
  /** Mensagens âncora, para destacar também no diálogo da conversa inteira. */
  messageIds?: string[];
}

/**
 * A conversa em volta das mensagens que sustentaram a conclusão da IA, para
 * quem está na ficha do freelancer ou na linha do tempo.
 *
 * Quem tem a tela do WhatsApp pode seguir para a conversa inteira; quem só tem
 * a ficha fica no trecho, que é o que sustenta a conclusão.
 */
export function WhatsAppTrechoInline({
  itemId,
  conversationId,
  messageIds = [],
}: WhatsAppTrechoInlineProps) {
  const [conversaCompleta, setConversaCompleta] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["whatsapp-trecho", itemId],
    queryFn: () => whatsappApi.trecho(itemId),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Carregando a conversa...
      </div>
    );
  }
  if (isError || !data) {
    return (
      <p className="py-2 text-xs text-muted-foreground">
        Não foi possível carregar o trecho desta conversa.
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-1 rounded-md border bg-muted/20 p-2">
      {data.mensagens.map((mensagem) => (
        <div
          key={mensagem.id}
          className={cn(
            "rounded px-2 py-1 text-xs",
            AUTOR_STYLES[mensagem.autor] ?? "bg-muted",
            mensagem.ancora && "ring-1 ring-primary/40",
          )}
        >
          <span className="font-medium">{mensagem.autor}</span>
          <span className="ml-2 text-muted-foreground">
            {formatDateTime(mensagem.created_at)}
          </span>
          <p className="mt-0.5 whitespace-pre-wrap wrap-break-word">{mensagem.texto}</p>
        </div>
      ))}
      {data.pode_ver_conversa_completa && (
        <>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => setConversaCompleta(true)}
          >
            Ver conversa inteira ({data.total_na_conversa} mensagens)
          </Button>
          <WhatsAppTranscriptDialog
            conversationId={conversaCompleta ? conversationId : null}
            highlightIds={messageIds}
            onClose={() => setConversaCompleta(false)}
          />
        </>
      )}
    </div>
  );
}
